import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share, PlusSquare } from "lucide-react";

// Detects whether the app is already running from the home screen (installed).
// Blindado para webviews que não implementam `matchMedia` ou `navigator.standalone`.
const isStandalone = () => {
  try {
    if (typeof window === "undefined") return false;
    const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
    if (mm && mm.matches) return true;
    return window.navigator && window.navigator.standalone === true;
  } catch { return false; }
};

const isIOS = () => {
  try { return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream; }
  catch { return false; }
};
const isAndroid = () => {
  try { return /Android/i.test(navigator.userAgent); }
  catch { return false; }
};

const DISMISS_KEY = "install_banner_dismissed_at";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Small, dismissible bottom banner that teaches mobile visitors how to add the
 * site to their home screen. Uses the native `beforeinstallprompt` on Android
 * Chrome/Edge (one-tap install) and shows Apple-specific instructions on iOS.
 */
export default function InstallBanner({ appName = "Visão Agenda", brandColor }) {
  const [visible, setVisible] = useState(false);
  const [promptEvent, setPromptEvent] = useState(null); // Android install prompt
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPromptEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    // iOS Safari never fires beforeinstallprompt — show the banner after a
    // small delay so it doesn't compete with the page's initial render.
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); clearTimeout(t); };
    }
    // For non-iOS mobile browsers that don't fire the event (some in-app browsers),
    // still show the generic helper after a while.
    if (isAndroid()) {
      const t = setTimeout(() => setVisible((v) => v || true), 3000);
      return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false); setShowIOSHelp(false);
  };

  const install = async () => {
    if (promptEvent) {
      try {
        await promptEvent.prompt();
        await promptEvent.userChoice;
        dismiss();
        return;
      } catch { /* fall through to iOS instructions */ }
    }
    setShowIOSHelp(true);
  };

  if (!visible) return null;
  const iosView = isIOS() || showIOSHelp;
  const bg = brandColor || "#0f1b16";

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[60] rounded-2xl shadow-2xl print:hidden animate-fade-in-up border border-white/10"
      style={{ background: bg, color: "#fff" }}
      data-testid="install-banner"
    >
      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Download className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight">Instale o {appName} no seu celular</div>
            <div className="text-xs opacity-90 mt-1">
              {iosView
                ? "Fica como um aplicativo na sua tela inicial — sem loja, sem app."
                : "Um toque para adicionar à tela inicial — parece um app de verdade."}
            </div>
          </div>
          <button onClick={dismiss} aria-label="Fechar" className="p-1 rounded-md hover:bg-white/10" data-testid="install-banner-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {iosView ? (
          <div className="rounded-xl bg-white/10 p-3 text-sm space-y-2">
            <div className="flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">1</span> Toque no botão <Share className="h-4 w-4 inline mx-1" /> <strong>Compartilhar</strong> abaixo no Safari.</div>
            <div className="flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">2</span> Escolha <strong>“Adicionar à Tela de Início”</strong> <PlusSquare className="h-4 w-4 inline mx-1" />.</div>
            <div className="flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">3</span> Toque em <strong>Adicionar</strong> e pronto ✨</div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button onClick={install} data-testid="install-banner-cta" className="flex-1 bg-white text-black hover:bg-white/90 h-11">
              <Download className="h-4 w-4 mr-2" /> Instalar agora
            </Button>
            <Button onClick={dismiss} variant="ghost" className="h-11 text-white hover:bg-white/10">Agora não</Button>
          </div>
        )}
      </div>
    </div>
  );
}
